import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import Moment from 'react-moment';

import SpinnerComponent from '../Common/Spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';

const MatchHistoryTitle = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: .1em;
`

const SpinnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 15em;
`

const GameListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 15em;
  max-height: 30em;
  overflow-y: scroll;
`

const GameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: .25em;
  background: #666;
  border: 1px solid #666;
`

const TopRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  max-height: 3em;
`

const MatchupSummaryRow = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
`

const MatchupSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 .1em;
  min-width: 3.25em;
  background: #666;
  color: white;
`

const TeamLogo = styled.img`
  max-height: 1.5em;
  min-width: 1.5em;
  max-width: 1.5em;
`

const RoundWins = styled.div`
  flex: 1;
  margin: auto .25em;
  text-align: center;
`

const PerformanceWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  max-height: 3em;
  background: white;
`

const PerformanceStat = styled.div`
  flex: 1;
  text-align: center;
  font-size: 1.2em;

  @media (max-width: 1550px) {
    font-size: 1em;
  }
`

const PerformanceStatLabel = styled.span`
  font-size: .8em;
`

const Agent = styled.img`
  max-height: 100%;
  margin-right: 1em;

  @media (max-width: 768px) {
    margin-right: .1em;
  }
`

const VODEtCeteraContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: center;
  padding: 0 1em;

  > svg {
    /* If there is no <a> wrapper (no VOD) */
    color: #ccc;
  }

  > a > svg {
    color: white;
  }

  &:hover {
    > a > svg {
      color: #228fd8;
    }
  }
`

const MetadataContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: .8em;
  background: #666;
  color: white;
`

const Map = styled.div`
  min-width: 4.5em;
  padding-left: .25em;
  font-weight: bold;
`

const EventName = styled.div`
  margin-left: .5em;
  font-style: italic;
`

const SeriesDate = styled.div``

const PlayerMatchHistory = ({ fetchedYet, fetching, games }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  useEffect(() => {
  }, [
    fetching,
    games
  ]);

  return (
    <>
      <MatchHistoryTitle>Match History</MatchHistoryTitle>
      {
        (fetching || !fetchedYet) ? (
          <SpinnerWrapper>
            <SpinnerComponent />
          </SpinnerWrapper>
        ) : (
          <GameListWrapper>
            {(games && games.length > 0) ? (
              games.map((game, idx) => {
                const rarValueToUse = game.roundsAboveReplacement !== null ? game.roundsAboveReplacement : game.agnosticRar;
                const rarString = rarValueToUse !== null ? `${rarValueToUse > 0 ? '+' : ''}${rarValueToUse.toFixed(1)}` : '---';

                return (
                  <GameWrapper
                    key={`game-${idx}`}
                  >
                    <TopRowContainer>
                      <MatchupSummaryContainer>
                        <MatchupSummaryRow>
                          <TeamLogo title={game.teamName} src={game.teamLogo} />
                          <RoundWins>{game.roundWins}</RoundWins>
                        </MatchupSummaryRow>
                        <MatchupSummaryRow>
                          <TeamLogo title={game.opponentTeamName} src={game.oppTeamLogo} />
                          <RoundWins>{game.roundLosses}</RoundWins>
                        </MatchupSummaryRow>
                      </MatchupSummaryContainer>
                      <PerformanceWrapper>
                        <Agent src={`https://media.valorant-api.com/agents/${game.agentId}/displayicon.png`} />
                        <PerformanceStat>
                          {rarString}
                          <PerformanceStatLabel>&nbsp;RAR</PerformanceStatLabel>
                        </PerformanceStat>
                        <PerformanceStat>
                          {(game.kast * 100).toFixed(isMobile ? 0 : 1)}
                          <PerformanceStatLabel>% KAST</PerformanceStatLabel>
                        </PerformanceStat>
                        <PerformanceStat>
                          {game.kills}/{game.deaths}
                          <PerformanceStatLabel>&nbsp;K/D</PerformanceStatLabel>
                        </PerformanceStat>
                        {!isMobile && (
                          <PerformanceStat>
                            {game.fk}/{game.ufd}
                            <PerformanceStatLabel>&nbsp;FK/UFD</PerformanceStatLabel>
                          </PerformanceStat>
                        )}
                      </PerformanceWrapper>
                      <VODEtCeteraContainer title={game.vod ? 'Broadcast VOD' : 'No VOD available'}>
                        {game.vod ? (
                          <a href={game.vod} target='_blank' rel='noopener noreferrer'>
                            <FontAwesomeIcon icon={faYoutube} />
                          </a>
                        ) : (
                          <FontAwesomeIcon icon={faYoutube} />
                        )}
                      </VODEtCeteraContainer>
                    </TopRowContainer>
                    <MetadataContainer>
                      <Map>
                        {game.mapName}
                      </Map>
                      <SeriesDate>
                        {<Moment
                          date={game.seriesDate}
                          format='MMM D, YYYY'
                        />}
                      </SeriesDate>
                      <EventName>
                        {game.league + ' ' + game.tournament || 'Unknown event'}
                      </EventName>
                    </MetadataContainer>
                  </GameWrapper>
                )
              })
            ) : (
              <div>No games to display; possible error with fetching data</div>
            )}
          </GameListWrapper>
        )
      }
    </>
  );
};

export default inject('AppStore')(observer(PlayerMatchHistory))